/**
 * PATH: apps/quote/src/components/builder/sheets/VisibleColumnsSection.tsx
 * FILE_ID: QB-SETUP-VISIBLECOLS-001
 * PURPOSE: Setup-tab section for toggling visibility of "advanced" optional
 *          columns (the SAM-parity 50-column expansion). Each toggle here
 *          persists to `quote.config.visible_columns` (string[]).
 * WHY: A blanket 34-column grid is too noisy for casual quotes (Bravada paint
 *      lines often only need the 17 baseline). Estimators opt-in advanced
 *      columns per-quote so simple jobs stay clean and complex jobs (steel
 *      coatings, multi-trade, etc.) can surface plant_unit_cost / subcon_days
 *      / total_cost / etc. without forking the schema.
 * FLOW:
 *   ┌──────────────────┐     ┌────────────────────────┐     ┌─────────────────┐
 *   │ Toggle column ON │ --> │ quote.config.visible_  │ --> │ Grid: optional  │
 *   │ in Setup tab     │     │ columns: string[]      │     │ tracks expand   │
 *   │                  │     │ (PATCH /quotes/<id>)   │     │ from 0px → real │
 *   └──────────────────┘     └────────────────────────┘     └─────────────────┘
 * DEPENDENCIES:
 *   - quoteApi.updateQuote — persists `quote.config.visible_columns`
 *   - QB-SCOPESHEET-COLUMNS-001 — applyOptionalColumnVisibility consumes this list
 * RELATED:
 *   - apps/quote/src/components/builder/scope-sheet-grid/scope-sheet-columns.ts
 *     (BASE_COLUMN_BANDS — the "advanced" band declares what's toggleable)
 */

import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { quoteApi, type QuoteDetail } from '@/lib/quoteApi'
import { BASE_COLUMN_BANDS } from '../scope-sheet-grid/scope-sheet-columns'
import { getEffectiveVisibleColumnIds } from '../scope-sheet-grid/visible-columns-helper'

interface VisibleColumnsSectionProps {
  quoteId: string
  quote: QuoteDetail
  isEditable: boolean
  defaultExpanded?: boolean
}

interface ToggleableColumn {
  id: string
  label: string
  type: 'text' | 'number' | 'currency'
  computed: boolean
}

function readVisibleColumnIds(quote: QuoteDetail): string[] {
  // Mirror the grid's effective-list logic so the Setup picker's toggle
  // state matches what the grid is actually rendering. See
  // QB-SCOPESHEET-VISIBLE-001 for the three-state semantics
  // (key absent → all-on, [] → user-cleared, [...] → exact list).
  return getEffectiveVisibleColumnIds(quote)
}

function readToggleableColumns(): ToggleableColumn[] {
  // Pull every `optional: true` column declared in BASE_COLUMN_BANDS. Today
  // that's the "advanced" band, but anything tagged optional anywhere will
  // surface here automatically.
  const out: ToggleableColumn[] = []
  for (const band of BASE_COLUMN_BANDS) {
    for (const col of band.columns) {
      if (col.optional) {
        out.push({
          id: col.id,
          label: col.label,
          type: (col.type as ToggleableColumn['type']) ?? 'text',
          computed: !!col.computed,
        })
      }
    }
  }
  return out
}

const PRESET_GROUPS: { id: string; label: string; columnIds: string[] }[] = [
  {
    id: 'cost-tracking',
    label: 'Cost tracking',
    columnIds: ['labour_cost', 'material_cost', 'plant_cost', 'subcon_cost', 'total_cost', 'margin'],
  },
  {
    id: 'plant-detail',
    label: 'Plant detail',
    columnIds: ['plant_description', 'plant_unit_cost', 'plant_quantity', 'plant_cost'],
  },
  {
    id: 'subcon-detail',
    label: 'Subcon detail',
    columnIds: ['subcon_description', 'subcon_quote', 'subcon_days', 'subcon_cost'],
  },
  {
    id: 'notes',
    label: 'Notes',
    columnIds: ['internal_notes', 'client_visible_notes'],
  },
]

export default function VisibleColumnsSection({
  quoteId,
  quote,
  isEditable,
  defaultExpanded = false,
}: VisibleColumnsSectionProps) {
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState(defaultExpanded)
  const allToggleable = useMemo(() => readToggleableColumns(), [])
  const visibleIds = useMemo(() => readVisibleColumnIds(quote), [quote])
  const visibleSet = useMemo(() => new Set(visibleIds), [visibleIds])

  const persist = useMutation({
    mutationFn: async (next: string[]) => {
      const cfg = (quote.config ?? {}) as Record<string, unknown>
      return quoteApi.updateQuote(quoteId, {
        config: { ...cfg, visible_columns: next },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', 'builder', quoteId] })
    },
  })

  const toggle = (colId: string) => {
    if (!isEditable) return
    const next = new Set(visibleSet)
    if (next.has(colId)) next.delete(colId)
    else next.add(colId)
    persist.mutate(Array.from(next))
  }

  const applyPreset = (ids: string[]) => {
    if (!isEditable) return
    const next = new Set(visibleSet)
    // Toggle behaviour: if every preset id is already on, turn them all off.
    // Otherwise turn them all on. Mirrors how Excel "All borders" behaves.
    const allOn = ids.every((id) => next.has(id))
    for (const id of ids) {
      if (allOn) next.delete(id)
      else next.add(id)
    }
    persist.mutate(Array.from(next))
  }

  const enabledCount = visibleIds.length
  const totalCount = allToggleable.length

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-800"
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
          <span className="font-semibold">Visible columns</span>
          <span className="text-xs opacity-70">
            • {enabledCount}/{totalCount} optional columns enabled
          </span>
        </div>
        <Eye className="w-4 h-4 opacity-70" />
      </button>

      {expanded && (
        <div className="bg-white dark:bg-slate-900 p-4 space-y-4">
          <div className="flex items-start gap-3 p-3 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded text-xs text-rose-800 dark:text-rose-200">
            <Eye className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Advanced columns are on by default — Excel-grade grid out of the box</p>
              <p className="opacity-80 mt-0.5">
                Per-line costs (labour/material/plant/subcon), plant + subcon
                detail fields, estimator notes, and derived totals (total cost,
                margin) all show up on a fresh quote. Toggle them off below if a
                particular quote doesn't need them — toggling here only affects
                this quote and doesn't change the underlying line-item data.
              </p>
            </div>
          </div>

          {/* Preset groups */}
          <div>
            <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wider">
              Quick presets
            </div>
            <div className="flex flex-wrap gap-2">
              {PRESET_GROUPS.map((group) => {
                const allOn = group.columnIds.every((id) => visibleSet.has(id))
                const someOn = !allOn && group.columnIds.some((id) => visibleSet.has(id))
                return (
                  <button
                    key={group.id}
                    onClick={() => applyPreset(group.columnIds)}
                    disabled={!isEditable || persist.isPending}
                    className={cn(
                      'px-3 py-1.5 rounded-md text-xs font-medium border transition-colors',
                      allOn
                        ? 'bg-rose-600 text-white border-rose-600 hover:bg-rose-700'
                        : someOn
                        ? 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-200 border-rose-300 dark:border-rose-700'
                        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700',
                      (!isEditable || persist.isPending) && 'opacity-50 cursor-not-allowed',
                    )}
                  >
                    {group.label}
                    <span className="ml-1.5 opacity-60">
                      ({group.columnIds.filter((id) => visibleSet.has(id)).length}/{group.columnIds.length})
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Per-column grid */}
          <div>
            <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wider">
              Individual columns
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {allToggleable.map((col) => {
                const on = visibleSet.has(col.id)
                return (
                  <button
                    key={col.id}
                    onClick={() => toggle(col.id)}
                    disabled={!isEditable || persist.isPending}
                    className={cn(
                      'flex items-center justify-between gap-2 px-3 py-2 rounded-md text-xs border transition-colors text-left',
                      on
                        ? 'bg-rose-50 dark:bg-rose-900/30 border-rose-300 dark:border-rose-700 text-rose-900 dark:text-rose-100'
                        : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700',
                      (!isEditable || persist.isPending) && 'opacity-50 cursor-not-allowed',
                    )}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {on ? (
                        <Eye className="w-3.5 h-3.5 flex-shrink-0 text-rose-500 dark:text-rose-300" />
                      ) : (
                        <EyeOff className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
                      )}
                      <span className="truncate font-medium">{col.label}</span>
                    </div>
                    <span
                      className={cn(
                        'flex-shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider',
                        col.computed
                          ? 'bg-amber-200 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200'
                          : col.type === 'currency'
                          ? 'bg-emerald-200 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-200'
                          : col.type === 'number'
                          ? 'bg-cyan-200 dark:bg-cyan-900/50 text-cyan-800 dark:text-cyan-200'
                          : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200',
                      )}
                    >
                      {col.computed ? 'calc' : col.type}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {persist.isPending && (
            <div className="text-xs text-slate-500 dark:text-slate-400 italic">
              Saving column visibility...
            </div>
          )}
        </div>
      )}
    </div>
  )
}
